import { Link, useLocation } from "wouter";
import { Activity, ShieldCheck } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/**
 * PLATFORM SECTION NAV — Overview ↔ Health.
 *
 * WHY THIS EXISTS. `/platform/health` shipped live but unreachable. The only
 * platform entry point was a single "Platform console" row on More, so an
 * administrator had to already know the URL to open the health page. This is
 * discoverability, nothing else.
 *
 * IT CARRIES NO AUTHORITY. It renders inside pages that already bounce a
 * non-platform-admin home, and every `platform.*` procedure is gated server
 * side by `platformAdminProcedure`. Visibility of a link is not a permission
 * and must never be treated as one — this component deliberately performs no
 * role test of its own, so there is no second, weaker gate to drift out of
 * step with the real one.
 *
 * ACTIVE STATE reuses the app's existing idiom — `aria-current="page"` plus the
 * coral accent, exactly as the bottom tab bar does — rather than inventing a
 * treatment for two links.
 *
 * The label is always rendered, never the icon alone: the icon is decorative
 * and is hidden from assistive technology.
 */
const ITEMS = [
  { href: "/platform", icon: ShieldCheck, en: "Overview", ar: "نظرة عامة", testId: "platform-nav-overview" },
  { href: "/platform/health", icon: Activity, en: "Health", ar: "الصحة", testId: "platform-nav-health" },
] as const;

export function PlatformNav() {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const [location] = useLocation();

  return (
    <nav
      data-testid="platform-nav"
      aria-label={isAr ? "أقسام المنصّة" : "Platform sections"}
      // flex-wrap, not a scroller: two short items must never push the page
      // sideways on a narrow screen.
      className="mb-4 flex flex-wrap items-center gap-1.5"
    >
      {ITEMS.map(item => {
        // Exact match: "/platform" must not light up while on "/platform/health".
        const active = location === item.href;
        const Icon = item.icon;
        return (
          <Link
            key={item.href}
            href={item.href}
            data-testid={item.testId}
            aria-current={active ? "page" : undefined}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
              active
                ? "border-coral/40 bg-coral-soft/70 text-coral"
                : "border-border/60 text-muted-foreground hover:bg-accent/50 hover:text-foreground",
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            {isAr ? item.ar : item.en}
          </Link>
        );
      })}
    </nav>
  );
}
